/**
 * Group chat cover-image adapter.
 *
 * Lets a group conversation use a custom picture instead of the default
 * composite member-avatar icon. UI components go through this module rather
 * than reading/writing storage directly, mirroring how the rest of chat is
 * isolated behind a service layer.
 *
 * Persistence: the chosen image (downscaled data URL) is stored on the AppSync
 * `Conversation.coverImageUrl` field so it survives reloads and syncs across
 * users/devices. localStorage is used only as a fast, reactive read-through
 * cache — `syncGroupCoverFromServer` keeps it in step with whatever the server
 * returns from `listConversations` / `getConversation` / subscriptions. The
 * actual write happens via the `updateConversation` mutation in the UI layer.
 */

const STORAGE_PREFIX = 'ascend-group-cover:'
const COVER_UPDATED_EVENT = 'ascend:group-cover-updated'

/** Longest edge of the stored image, in px (keeps localStorage payloads small). */
const MAX_COVER_DIMENSION = 256
const JPEG_QUALITY = 0.82

function storageKey(conversationId: string): string {
  return `${STORAGE_PREFIX}${conversationId}`
}

function emitCoverUpdated(conversationId: string): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent<{ conversationId: string }>(COVER_UPDATED_EVENT, {
      detail: { conversationId },
    }),
  )
}

export function getGroupCoverImage(conversationId: string): string | null {
  if (typeof window === 'undefined' || !conversationId) return null
  try {
    return window.localStorage.getItem(storageKey(conversationId))
  } catch {
    return null
  }
}

export function setGroupCoverImage(conversationId: string, dataUrl: string): void {
  if (typeof window === 'undefined' || !conversationId) return
  try {
    window.localStorage.setItem(storageKey(conversationId), dataUrl)
    emitCoverUpdated(conversationId)
  } catch {
    throw new Error('Could not save the group photo — your browser storage may be full.')
  }
}

/**
 * Reconcile the local cache with the authoritative server value. Call whenever
 * a conversation is fetched/streamed from AppSync. Only writes (and emits) when
 * the value actually changed, so it is safe to call on every poll/render.
 */
export function syncGroupCoverFromServer(
  conversationId: string,
  serverValue: string | null | undefined,
): void {
  if (typeof window === 'undefined' || !conversationId) return
  const next = serverValue && serverValue.trim() !== '' ? serverValue : null
  const current = getGroupCoverImage(conversationId)
  if (current === next) return
  try {
    if (next) {
      window.localStorage.setItem(storageKey(conversationId), next)
    } else {
      window.localStorage.removeItem(storageKey(conversationId))
    }
    emitCoverUpdated(conversationId)
  } catch {
    /* cache is best-effort; ignore quota/availability errors */
  }
}

export function clearGroupCoverImage(conversationId: string): void {
  if (typeof window === 'undefined' || !conversationId) return
  try {
    window.localStorage.removeItem(storageKey(conversationId))
    emitCoverUpdated(conversationId)
  } catch {
    /* ignore */
  }
}

/** Subscribe to cover-image changes for any conversation. Returns an unsubscribe fn. */
export function onGroupCoverUpdated(
  handler: (conversationId: string) => void,
): () => void {
  if (typeof window === 'undefined') return () => {}
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<{ conversationId?: string }>).detail
    if (detail?.conversationId) handler(detail.conversationId)
  }
  window.addEventListener(COVER_UPDATED_EVENT, listener)
  return () => window.removeEventListener(COVER_UPDATED_EVENT, listener)
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('That image could not be read. Try a different file.'))
    image.src = src
  })
}

/**
 * Read an image file and return a square, downscaled JPEG data URL suitable for
 * a circular group avatar. Center-crops to a square so it fills the avatar.
 */
export async function fileToCoverDataUrl(
  file: File,
  maxDimension = MAX_COVER_DIMENSION,
): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please choose an image file (JPG, PNG, GIF, or WebP).')
  }
  const objectUrl = URL.createObjectURL(file)
  try {
    const image = await loadImageElement(objectUrl)
    const side = Math.min(image.naturalWidth, image.naturalHeight) || maxDimension
    const target = Math.min(side, maxDimension)
    const canvas = document.createElement('canvas')
    canvas.width = target
    canvas.height = target
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not process the image in this browser.')
    const sx = (image.naturalWidth - side) / 2
    const sy = (image.naturalHeight - side) / 2
    ctx.drawImage(image, sx, sy, side, side, 0, 0, target, target)
    return canvas.toDataURL('image/jpeg', JPEG_QUALITY)
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}
